// src/pages/Register/RegisterPage.jsx
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaCheck,
  FaEnvelope,
  FaEye,
  FaEyeSlash,
  FaLock,
  FaShieldAlt,
  FaTimes,
  FaTrophy,
  FaUser,
  FaUserPlus,
} from "react-icons/fa";

import backgroundImage from "./HeaderSlider_02.jpg";
import useScrollToTop from "./../../hooks/useScrollToTop";
import { registerUser, checkEmailExists } from "../../services/userServices";
import { sendNotificationToAdmin } from "../../services/notificationServices";

const INPUT =
  "w-full rounded-2xl border border-slate-200 bg-white/90 px-4 py-3 text-sm font-medium text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950/80 dark:text-slate-100 dark:focus:border-indigo-500 dark:focus:ring-indigo-950";

function getPasswordScore(password) {
  let score = 0;

  if (password.length >= 6) score += 1;
  if (password.length >= 10) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;

  return Math.min(score, 5);
}

function getPasswordLabel(score) {
  if (score <= 1) return "Faible";
  if (score <= 3) return "Correct";
  if (score === 4) return "Bon";
  return "Très bon";
}

export default function RegisterPage() {
  useScrollToTop();

  const navigate = useNavigate();

  const [form, setForm] = useState({
    nom: "",
    prenom: "",
    email: "",
    password: "",
  });

  const [showPwd, setShowPwd] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isEmailModalOpen, setEmailModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const passwordScore = useMemo(
    () => getPasswordScore(form.password),
    [form.password]
  );

  const isFormValid = useMemo(() => {
    return (
      form.nom.trim().length >= 2 &&
      form.prenom.trim().length >= 2 &&
      form.email.trim().includes("@") &&
      form.password.length >= 6
    );
  }, [form]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errorMessage) {
      setErrorMessage("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");

    if (!isFormValid) {
      setErrorMessage(
        "Merci de renseigner votre nom, prénom, email et un mot de passe d’au moins 6 caractères."
      );
      return;
    }

    try {
      setLoading(true);

      const cleanEmail = form.email.trim().toLowerCase();
      const cleanNom = form.nom.trim();
      const cleanPrenom = form.prenom.trim();

      const emailCheck = await checkEmailExists(cleanEmail);

      if (emailCheck?.error) {
        setErrorMessage(emailCheck.error);
        return;
      }

      if (emailCheck?.exists) {
        setEmailModalOpen(true);
        return;
      }

      const result = await registerUser(
        cleanNom,
        cleanPrenom,
        cleanEmail,
        form.password
      );

      if (result?.error) {
        setErrorMessage(result.error);
        return;
      }

      try {
        const token = localStorage.getItem("token");
        const message = `Un nouvel utilisateur a été créé : ${cleanNom} ${cleanPrenom} (${cleanEmail})`;
        await sendNotificationToAdmin(message, token);
      } catch (notificationError) {
        console.warn("Notification admin non envoyée :", notificationError);
      }

      navigate("/login", {
        state: {
          registered: true,
          email: cleanEmail,
        },
      });
    } catch (error) {
      console.error("Erreur inscription :", error);
      setErrorMessage(
        "Une erreur est survenue pendant l’inscription. Merci de réessayer."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = () => {
    setEmailModalOpen(false);
    navigate("/forgot-password", {
      state: {
        email: form.email.trim().toLowerCase(),
      },
    });
  };

  return (
    <main
      className="relative min-h-screen overflow-hidden bg-slate-950 bg-cover bg-center px-3 py-8 pt-[120px] text-slate-900 dark:text-white sm:px-5 md:pt-[145px]"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-[2px]" />
      <div className="absolute -right-24 top-20 h-72 w-72 rounded-full bg-indigo-500/30 blur-3xl" />
      <div className="absolute bottom-0 left-10 h-72 w-72 rounded-full bg-pink-500/20 blur-3xl" />
      <div className="absolute left-1/2 top-1/2 h-80 w-80 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500/10 blur-3xl" />

      <div className="relative mx-auto grid w-full max-w-[1180px] gap-6 lg:grid-cols-[minmax(0,1fr)_460px] lg:items-center">
        <section className="hidden text-white lg:block">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-white/80 backdrop-blur">
            <FaTrophy />
            MonAppliTournoi
          </div>

          <h1 className="mt-5 max-w-3xl text-5xl font-bold tracking-tight">
            Créez votre compte et organisez vos compétitions plus simplement.
          </h1>

          <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300">
            Gérez les inscriptions, les pesées, les poules, les tapis, les
            combats, les résultats publics et les QR Codes depuis une interface
            pensée pour les clubs de judo.
          </p>

          <div className="mt-8 grid max-w-3xl grid-cols-1 gap-3 sm:grid-cols-3">
            <HeroFeature
              icon={FaCheck}
              title="Démo disponible"
              text="Testez avant d’acheter."
            />
            <HeroFeature
              icon={FaShieldAlt}
              title="Compte sécurisé"
              text="Accès personnel protégé."
            />
            <HeroFeature
              icon={FaTrophy}
              title="Spécial Judo"
              text="Pensé pour les tournois."
            />
          </div>
        </section>

        <section className="mx-auto w-full max-w-xl lg:max-w-none">
          <div className="overflow-hidden rounded-[34px] border border-white/20 bg-white/95 shadow-2xl backdrop-blur-xl dark:border-slate-800 dark:bg-slate-900/95">
            <div className="relative overflow-hidden bg-slate-950 p-6 text-white sm:p-8">
              <div className="absolute -right-14 -top-14 h-40 w-40 rounded-full bg-indigo-500/30 blur-3xl" />
              <div className="absolute -bottom-16 left-1/3 h-40 w-40 rounded-full bg-pink-500/20 blur-3xl" />

              <div className="relative">
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-xl">
                  <FaUserPlus />
                </div>

                <h2 className="text-3xl font-bold tracking-tight">
                  Inscription
                </h2>

                <p className="mt-2 text-sm leading-relaxed text-slate-300">
                  Créez votre compte gratuitement. Vous pourrez ensuite choisir
                  une formule ou tester le mode démo.
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 p-5 sm:p-7">
              {errorMessage ? (
                <div
                  className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300"
                  role="alert"
                >
                  {errorMessage}
                </div>
              ) : null}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Nom" icon={FaUser}>
                  <input
                    type="text"
                    name="nom"
                    placeholder="Votre nom"
                    className={INPUT}
                    value={form.nom}
                    onChange={handleChange}
                    autoComplete="family-name"
                    required
                  />
                </Field>

                <Field label="Prénom" icon={FaUser}>
                  <input
                    type="text"
                    name="prenom"
                    placeholder="Votre prénom"
                    className={INPUT}
                    value={form.prenom}
                    onChange={handleChange}
                    autoComplete="given-name"
                    required
                  />
                </Field>
              </div>

              <Field label="Email" icon={FaEnvelope}>
                <input
                  type="email"
                  name="email"
                  placeholder="votre.email@exemple.fr"
                  className={INPUT}
                  value={form.email}
                  onChange={handleChange}
                  autoComplete="email"
                  required
                />
              </Field>

              <Field label="Mot de passe" icon={FaLock}>
                <div className="relative">
                  <input
                    type={showPwd ? "text" : "password"}
                    name="password"
                    placeholder="Au moins 6 caractères"
                    className={`${INPUT} pr-12`}
                    value={form.password}
                    onChange={handleChange}
                    autoComplete="new-password"
                    minLength={6}
                    required
                  />

                  <button
                    type="button"
                    onClick={() => setShowPwd((value) => !value)}
                    aria-label={
                      showPwd
                        ? "Masquer le mot de passe"
                        : "Afficher le mot de passe"
                    }
                    aria-pressed={showPwd}
                    title={showPwd ? "Masquer" : "Afficher"}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 transition hover:text-slate-800 dark:text-slate-300 dark:hover:text-white"
                  >
                    {showPwd ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>

                <PasswordStrength score={passwordScore} password={form.password} />
              </Field>

              <button
                type="submit"
                disabled={loading || !isFormValid}
                className="group inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 via-violet-600 to-pink-500 px-4 py-3.5 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
              >
                <FaUserPlus />
                {loading ? "Création du compte..." : "Créer mon compte"}
                {!loading ? (
                  <FaArrowRight className="transition group-hover:translate-x-1" />
                ) : null}
              </button>

              <div className="rounded-2xl bg-slate-50 p-4 text-center text-sm text-slate-600 dark:bg-slate-950/60 dark:text-slate-300">
                Déjà un compte ?{" "}
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="font-bold text-indigo-600 hover:underline dark:text-indigo-300"
                >
                  Se connecter
                </button>
              </div>
            </form>
          </div>
        </section>
      </div>

      <ExistingEmailDialog
        isOpen={isEmailModalOpen}
        email={form.email}
        onClose={() => setEmailModalOpen(false)}
        onResetPassword={handleResetPassword}
      />
    </main>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <span className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200">
        <Icon className="text-indigo-500" />
        {label}
      </span>

      {children}
    </label>
  );
}

function PasswordStrength({ score, password }) {
  if (!password) {
    return (
      <p className="mt-2 text-xs font-medium text-slate-400">
        Utilisez au moins 6 caractères. Ajoutez chiffres, majuscules ou symboles
        pour renforcer votre mot de passe.
      </p>
    );
  }

  const label = getPasswordLabel(score);
  const percent = `${(score / 5) * 100}%`;

  const barClass =
    score <= 1
      ? "bg-red-500"
      : score <= 3
      ? "bg-amber-500"
      : "bg-emerald-500";

  return (
    <div className="mt-3">
      <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        <div
          className={`h-full rounded-full transition-all ${barClass}`}
          style={{ width: percent }}
        />
      </div>

      <div className="mt-2 flex items-center justify-between text-xs">
        <span className="font-semibold text-slate-500 dark:text-slate-400">
          Sécurité du mot de passe
        </span>
        <span className="font-bold text-slate-700 dark:text-slate-200">
          {label}
        </span>
      </div>
    </div>
  );
}

function HeroFeature({ icon: Icon, title, text }) {
  return (
    <div className="rounded-[24px] border border-white/10 bg-white/[0.08] p-4 backdrop-blur">
      <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10">
        <Icon />
      </div>

      <h3 className="font-bold text-white">{title}</h3>
      <p className="mt-1 text-sm leading-relaxed text-slate-300">{text}</p>
    </div>
  );
}

function ExistingEmailDialog({ isOpen, email, onClose, onResetPassword }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <div className="relative bg-slate-950 p-5 text-white">
          <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-indigo-500/30 blur-3xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div>
              <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10">
                <FaEnvelope />
              </div>

              <h2 className="text-xl font-bold">Email déjà utilisé</h2>

              <p className="mt-1 text-sm text-slate-300">
                Un compte existe déjà avec cette adresse.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-white/10 text-white transition hover:bg-white/20"
              aria-label="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="p-5">
          <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">
            L’adresse{" "}
            <span className="font-bold text-slate-900 dark:text-white">
              {email}
            </span>{" "}
            est déjà enregistrée. Vous pouvez réinitialiser votre mot de passe
            pour récupérer l’accès à votre compte.
          </p>

          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center rounded-2xl bg-slate-100 px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
            >
              Annuler
            </button>

            <button
              type="button"
              onClick={onResetPassword}
              className="inline-flex items-center justify-center rounded-2xl bg-indigo-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-700"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}